import { useRef, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

import ROUTES from '../../routes';
import api from '../../config/axiosInstance';

import { showToast } from '../../utils/toast';
import { handleErrors } from '../../utils/common';

const AddSeo = () => {
    const formRef = useRef(null);
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        page_code: '',
        page_title: '',
        hero_section_heading: '',
        hero_section_description: '',
        hero_button_text: '',
        meta_description: '',
        meta_keywords: '',
        index: '',
        include_in_sitemap: ''
    });

    const [submitting, setSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let finalValue = value;

        if (type === 'checkbox') {
            finalValue = checked ? 'true' : '';
        }

        setFormData((prev) => ({ ...prev, [name]: finalValue }));
    };

    useEffect(() => {
        const $ = window.jQuery;

        $.validator.addMethod("ckeditorRequired", function (value, element) {
            const textOnly = value.replace(/<[^>]*>/g, '').trim();
            return textOnly.length > 0;
        }, "Description is required");

        if (formRef.current && $.fn.validate) {
            $(formRef.current).validate({
                ignore: ".ck-editor *",
                rules: {
                    page_code: { required: true },
                    page_title: { required: true },
                    hero_section_heading: { required: true },
                    hero_section_description: { required: true, ckeditorRequired: true },
                    hero_button_text: { required: true },
                    meta_description: { required: true, ckeditorRequired: true },
                    meta_keywords: { required: true }
                },
                messages: {
                    page_code: { required: "Please select page code" },
                    page_title: { required: "Please enter page title" },
                    hero_section_heading: { required: "Please enter hero section heading" },
                    hero_section_description: { required: "Please enter hero section description" },
                    hero_button_text: { required: "Please enter hero button text" },
                    meta_description: { required: "Please enter meta description" },
                    meta_keywords: { required: "Please enter meta keywords" }
                },
                errorElement: "label",
                errorClass: "is-invalid text-danger",
                validClass: "is-valid",
                highlight: (el, err, valid) => $(el).addClass(err).removeClass(valid),
                unhighlight: (el, err, valid) => $(el).removeClass(err).addClass(valid)
            });
        }
    }, []);

    const handleFormSubmit = async () => {
        const $ = window.jQuery;
        const form = $(formRef.current);

        if (form.valid()) {
            try {
                setSubmitting(true);
                const res = await api.post('/seo/create', formData, {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });

                showToast('success', res.data.message || 'SEO saved successfully', 'Success');
                navigate(ROUTES.SEO);
            } catch (err) {
                handleErrors(err);
            } finally {
                setSubmitting(false);
            }
        }
    };

    return (
        <>
            <div className="page-titles">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to={ROUTES.SEO}>SEO</Link>
                    </li>
                    <li className="breadcrumb-item active">
                        <Link to={ROUTES.ADD_SEO}>Add SEO</Link>
                    </li>
                </ol>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header">
                            <h4 className="card-title">Add SEO</h4>
                        </div>
                        <div className="card-body">
                            <form ref={formRef} noValidate>
                                <div className="row">

                                    <div className="mb-3 col-md-6">
                                        <label htmlFor="page_code">Page Code</label>
                                        <select
                                            className="form-select"
                                            name="page_code"
                                            id="page_code"
                                            value={formData.page_code}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Page</option>
                                            <option value="home">Home</option>
                                            <option value="about">About</option>
                                            <option value="contact">Contact</option>
                                            <option value="career">Career</option>
                                            <option value="project">Projects</option>
                                        </select>
                                    </div>

                                    <div className="mb-3 col-md-6">
                                        <label htmlFor="page_title">Page Title</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="page_title"
                                            name="page_title"
                                            value={formData.page_title}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="mb-3 col-md-6">
                                        <label htmlFor="hero_section_heading">Hero Section Heading</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="hero_section_heading"
                                            name="hero_section_heading"
                                            value={formData.hero_section_heading}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="mb-3 col-md-6">
                                        <label htmlFor="hero_button_text">Hero Button Text</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="hero_button_text"
                                            name="hero_button_text"
                                            value={formData.hero_button_text}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="mb-3 col-md-6">
                                        <label htmlFor="meta_keywords">Meta Keywords</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="meta_keywords"
                                            name="meta_keywords"
                                            value={formData.meta_keywords}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="mb-3 col-md-3 d-flex align-items-center">
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="index"
                                                name="index"
                                                checked={formData.index === 'true'}
                                                onChange={handleChange}
                                            />
                                            <label className="form-check-label" htmlFor="index">Index</label>
                                        </div>
                                    </div>

                                    <div className="mb-3 col-md-3 d-flex align-items-center">
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="include_in_sitemap"
                                                name="include_in_sitemap"
                                                checked={formData.include_in_sitemap === 'true'}
                                                onChange={handleChange}
                                            />
                                            <label className="form-check-label" htmlFor="include_in_sitemap">Include in Sitemap</label>
                                        </div>
                                    </div>

                                    {[
                                        { name: 'meta_description', label: 'Meta Description' },
                                        { name: 'hero_section_description', label: 'Hero Section Description' }
                                    ].map(({ name, label }) => (
                                        <div key={name} className="mb-3 col-lg-6 col-md-6 col-sm-6">
                                            <div className="mb-3 col-lg-12">
                                                <label className="form-label">{label}</label>
                                                <CKEditor
                                                    editor={ClassicEditor}
                                                    config={{
                                                        toolbar: [
                                                            'heading', '|',
                                                            'bold', 'italic', 'link',
                                                            '|', 'bulletedList', 'numberedList'
                                                        ]
                                                    }}
                                                    data={formData[name]}
                                                    onChange={(event, editor) => {
                                                        const data = editor.getData();
                                                        setFormData(prev => ({ ...prev, [name]: data }));
                                                        document.querySelector(`input[name="${name}"]`).value = data; // sync with hidden input
                                                    }}
                                                />
                                                <input
                                                    type="hidden"
                                                    name={name}
                                                    value={formData[name]}
                                                />
                                            </div>
                                        </div>
                                    ))}


                                </div>

                                <div className="pt-3">
                                    <button
                                        type="button"
                                        onClick={handleFormSubmit}
                                        className="btn btn-sm btn-primary me-2"
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <>
                                                <i className="spinner-border spinner-border-sm me-1"></i>
                                                Submitting
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa fa-floppy-disk me-1"></i>
                                                Submit
                                            </>
                                        )}
                                    </button>
                                    <Link to={ROUTES.SEO} className="btn btn-sm btn-danger light">
                                        <i className="fas fa-times-circle"></i>&nbsp;
                                        Cancel
                                    </Link>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AddSeo;
